import { useCallback, useEffect, useRef, useState } from "react";
import { isActiveEmp } from "../lib/constants";
import { UploadTracker } from "./UploadTracker";
import { ArrowRight, Upload, Trash2, Users, UserCheck, Loader2, ChevronLeft, Medal, Printer, Target, Check, X, Wallet } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./ui/button";

const FOCUS = ["delivered", "rejected", "on_time", "avg_duration", "late"];
const SHORT = { delivered: "المهام المسلّمة", rejected: "المهام المرفوضة", on_time: "التسليم في الوقت", avg_duration: "متوسط مدة التوصيل", late: "الطلبات المتأخرة" };
const TONE = { delivered: "text-emerald-700", rejected: "text-rose-600", on_time: "text-blue-700", avg_duration: "text-amber-700", late: "text-orange-600" };
const TAGS = { none: "بدون مشرف", inactive: "غير نشط" };
const n = (v, d = 0) => (v == null ? "—" : Number(v).toLocaleString("en-US", { maximumFractionDigits: d }));
export const fmtMetric = (v, rate, label = "") => {
  if (v == null) return "—";
  if (rate) return `${n(v * 100, 2)}%`;
  if (/متوسط|كفاءة/.test(label)) return n(v, 2);
  return n(v, 0);
};
const fmtFocus = (k, v) => (k === "on_time" ? fmtMetric(v, true) : k === "avg_duration" ? (v == null ? "—" : `${n(v, 1)} د`) : n(v));

const MEDALS = { 1: ["ذهبي", "bg-amber-400 text-amber-950", "#F59E0B"], 2: ["فضي", "bg-slate-300 text-slate-800", "#94A3B8"], 3: ["برونزي", "bg-orange-300 text-orange-950", "#C2410C"] };
const RankBadge = ({ rank, name }) => {
  if (!rank) return null;
  const m = MEDALS[rank];
  return m ? <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${m[1]}`} data-testid={`ksup-rank-${name}`}><Medal className="h-3 w-3" />{m[0]}</span>
    : <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500" data-testid={`ksup-rank-${name}`}>#{rank}</span>;
};

const Blur = ({ id }) => <span data-testid={id} title="يظهر للمالك والمحاسب فقط" className="select-none blur-[5px]">000,000.00</span>;
const TeamIncome = ({ s, id }) => (s.team_income == null ? null : (
  <div className="mt-0.5 flex items-center gap-1 text-[11px] font-bold text-emerald-700" data-testid={id} title="مجموع «إجمالي المبلغ المستحق» لمناديب المشرف من فاتورة كيتا لهذا الشهر">
    <Wallet className="h-3 w-3" /> دخل الفريق من الفاتورة: {s.income_hidden ? <Blur id={`${id}-hidden`} /> : n(s.team_income, 2)} ر.س <span className="font-normal text-slate-400">({n(s.team_income_riders)} مندوب)</span>
  </div>
));

const GOOD_HIGH = { delivered: true, on_time: true, team_income: true };
const TotalsDrill = ({ k, list, onClose, onPick }) => {
  const isInc = k === "team_income";
  const hidden = (list || []).some((s) => s.income_hidden);
  const rows = (list || []).filter((s) => !s.tag).map((s) => ({ name: s.name, v: isInc ? s.team_income : s.focus?.[k], riders: s.riders_count }))
    .filter((r) => r.v != null).sort((a, b) => (GOOD_HIGH[k] ? b.v - a.v : a.v - b.v));
  const width = (r) => (isInc && hidden ? 60 : (Math.abs(Number(r.v) || 0) / max) * 100);
  const max = Math.max(1, ...rows.map((r) => Math.abs(Number(r.v) || 0)));
  const fmt = (v) => (isInc ? `${n(v, 2)} ر.س` : fmtFocus(k, v));
  return (
    <Dialog open={!!k} onOpenChange={(v) => !v && onClose()}>
      <DialogContent dir="rtl" className="max-h-[85vh] max-w-lg overflow-y-auto" data-testid="ksup-totals-drill">
        <DialogHeader className="text-start"><DialogTitle>{isInc ? "دخل الفريق من الفاتورة" : SHORT[k]} — حسب المشرف</DialogTitle>
          <DialogDescription className="text-start">مرتّب من الأفضل للأضعف · اضغط على المشرف لفتح صفحته</DialogDescription></DialogHeader>
        <div className="space-y-1.5">
          {!rows.length ? <p className="py-4 text-center text-xs text-slate-400">لا توجد بيانات</p> : rows.map((r, i) => (
            <button type="button" key={r.name} onClick={() => onPick(r.name)} data-testid={`ksup-totals-drill-${r.name}`}
              className="flex w-full items-center gap-2 rounded-lg px-1 py-1 text-xs hover:bg-slate-50">
              <span className="w-5 shrink-0 text-slate-400">{i + 1}</span>
              <span className="w-28 shrink-0 truncate text-start font-semibold text-slate-700">{r.name}</span>
              <div className="h-4 flex-1 overflow-hidden rounded bg-slate-100"><div className={`h-full rounded ${i === 0 ? "bg-emerald-400" : "bg-blue-300"}`} style={{ width: `${width(r)}%` }} /></div>
              <span className="w-24 text-left font-semibold tabular-nums">{isInc && hidden ? <Blur /> : fmt(r.v)}</span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Podium = ({ list, onOpen }) => {
  const top = list.filter((s) => s.rank && s.rank <= 3).sort((a, b) => a.rank - b.rank);
  if (!top.length) return null;
  return (
    <div className="rounded-2xl border border-amber-200 bg-gradient-to-l from-amber-50 to-white p-4" data-testid="ksup-podium">
      <p className="mb-3 flex items-center gap-1.5 font-heading text-sm font-bold text-slate-800"><Medal className="h-4 w-4 text-amber-500" /> ترتيب المشرفين هذا الشهر <span className="text-[11px] font-normal text-slate-400">(حسب نسبة التسليم في الوقت، ثم نسبة الطلبات المتأخرة)</span></p>
      <div className="grid gap-2 sm:grid-cols-3">{top.map((s) => (
        <button type="button" key={s.name} onClick={() => onOpen(s.name)} data-testid={`ksup-rank-${s.name}`} className="flex items-center gap-3 rounded-xl border border-white bg-white/80 p-3 text-start shadow-sm transition hover:border-amber-300 hover:shadow-md">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-lg font-black text-white" style={{ background: MEDALS[s.rank][2] }}>{s.rank}</div>
          <div className="min-w-0"><div className="truncate font-bold text-slate-800">{s.name}</div>
            <div className="text-[11px] text-slate-500">في الوقت {fmtFocus("on_time", s.focus.on_time)} · متأخرة {n(s.focus.late)} ({n((s.late_rate || 0) * 100, 2)}%)</div>
              <div className="mt-0.5 flex items-center gap-1 text-[11px] font-semibold text-indigo-700" data-testid={`ksup-rank-riders-${s.name}`}><Users className="h-3 w-3" /> {n(s.riders_count || 0)} مندوب تحت إدارته</div>
              <TeamIncome s={s} id={`ksup-rank-income-${s.name}`} /></div>
        </button>
      ))}</div>
    </div>
  );
};

const LOWER = new Set(["rejected", "avg_duration", "late"]);
const TBar = ({ p, k, testid }) => {
  if (!p) return null;
  return (
    <div className="mt-1.5" data-testid={testid}>
      <div className="h-1.5 overflow-hidden rounded-full bg-slate-200"><div className={`h-full rounded-full ${p.met ? "bg-emerald-500" : "bg-rose-400"}`} style={{ width: `${Math.min(100, p.pct * 100)}%` }} /></div>
      <div className={`mt-0.5 flex items-center justify-center gap-0.5 text-[9px] font-semibold ${p.met ? "text-emerald-600" : "text-rose-500"}`}>
        {p.met ? <Check className="h-2.5 w-2.5" /> : <X className="h-2.5 w-2.5" />}<span dir="ltr">{LOWER.has(k) ? "≤" : "≥"} {fmtFocus(k, p.target)}</span>
      </div>
    </div>
  );
};
const metCount = (t) => { const v = Object.values(t || {}); return v.length ? `${v.filter((x) => x.met).length}/${v.length}` : null; };

const toInput = (k, v) => (v == null ? "" : k === "on_time" ? String(+(v * 100).toFixed(2)) : String(v));
const TargetsDialog = ({ open, onClose, platform, month, names, onSaved }) => {
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.keetaSupTargets(platform, month).then((d) => {
      const o = {};
      ["*", ...names].forEach((nm) => { o[nm] = Object.fromEntries(FOCUS.map((k) => [k, toInput(k, d.targets[nm]?.[k])])); });
      setT(o);
    }).catch(() => toast.error("تعذّر التحميل"));
  }, [open, platform, month, names.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    setBusy(true);
    try {
      for (const nm of Object.keys(t)) {
        const targets = Object.fromEntries(FOCUS.filter((k) => t[nm][k] !== "").map((k) => [k, k === "on_time" ? Number(t[nm][k]) / 100 : Number(t[nm][k])]));
        await api.keetaSupTargetSave(platform, { month, name: nm, targets });
      }
      toast.success("تم حفظ الأهداف"); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl" dir="rtl" data-testid="ksup-targets-dialog">
        <DialogHeader className="text-right"><DialogTitle className="font-heading">أهداف المشرفين · {month}</DialogTitle>
          <DialogDescription className="text-right">«الهدف العام» ينطبق على كل مشرف ما له هدف خاص. الأهداف تستمر للأشهر الجاية لين تغيّرها. اترك الخانة فاضية إذا ما تبي هدف لها.</DialogDescription></DialogHeader>
        {!t ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-300" /> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[620px] text-xs">
              <thead><tr className="text-slate-500"><th className="px-2 py-2 text-right">المشرف</th>{FOCUS.map((k) => <th key={k} className="px-1 py-2 text-right font-medium">{SHORT[k]} <span className="text-slate-400">{LOWER.has(k) ? "(حد أعلى)" : "(حد أدنى)"}{k === "on_time" ? " %" : k === "avg_duration" ? " د" : ""}</span></th>)}</tr></thead>
              <tbody>{Object.keys(t).map((nm) => (
                <tr key={nm} className={`border-t border-slate-100 ${nm === "*" ? "bg-amber-50/60" : ""}`}>
                  <td className="whitespace-nowrap px-2 py-1.5 font-semibold">{nm === "*" ? "الهدف العام" : nm}</td>
                  {FOCUS.map((k) => (
                    <td key={k} className="px-1 py-1.5"><input type="number" min={0} step="any" dir="ltr" value={t[nm][k]} placeholder={nm === "*" ? "—" : "العام"}
                      onChange={(e) => setT({ ...t, [nm]: { ...t[nm], [k]: e.target.value } })}
                      className="h-8 w-full rounded-md border border-slate-200 px-2 text-xs" data-testid={`ksup-target-${nm === "*" ? "all" : nm}-${k}`} /></td>
                  ))}
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
        <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose}>إلغاء</Button><Button onClick={save} disabled={!t || busy} data-testid="ksup-targets-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} حفظ الأهداف</Button></div>
      </DialogContent>
    </Dialog>
  );
};

const MonthSel = ({ months, value, onChange }) => (
  <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm" data-testid="ksup-month">
    {(months.length ? months : [value]).filter(Boolean).map((m) => <option key={m} value={m}>{m}</option>)}
  </select>
);

const SupCard = ({ s, onOpen }) => (
  <button onClick={onOpen} className={`group w-full rounded-2xl border bg-white p-4 text-right shadow-sm transition-colors hover:border-amber-300 ${s.tag ? "border-dashed border-slate-300 opacity-80" : "border-slate-200"}`} data-testid={`ksup-card-${s.name}`}>
    <div className="flex items-center justify-between gap-2">
      <div className="min-w-0">
        <div className="flex items-center gap-2"><span className="truncate font-heading text-base font-bold text-slate-800">{s.name}</span>
          {s.tag && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500" data-testid={`ksup-tag-${s.name}`}>{TAGS[s.tag]}</span>}
          <RankBadge rank={s.rank} name={s.name} /></div>
        <p className="mt-0.5 text-[11px] text-slate-400">{metCount(s.targets) && <span className="ml-1 rounded bg-slate-100 px-1 font-semibold text-slate-600" data-testid={`ksup-met-${s.name}`}>الأهداف {metCount(s.targets)}</span>}{s.employee_name ? <span className="text-emerald-600">مربوط: {s.employee_name}</span> : "غير مربوط بموظف"} · {s.days} يوم · <span className="font-semibold text-indigo-700" data-testid={`ksup-riders-count-${s.name}`}>{n(s.riders_count || 0)} مندوب</span></p>
        <TeamIncome s={s} id={`ksup-income-${s.name}`} />
      </div>
      <ChevronLeft className="h-4 w-4 shrink-0 text-slate-300 group-hover:text-amber-500" />
    </div>
    <div className="mt-3 grid grid-cols-5 gap-1.5">
      {FOCUS.map((k) => (
        <div key={k} className="rounded-lg bg-slate-50 px-1.5 py-2 text-center">
          <div className={`text-sm font-bold tabular-nums ${TONE[k]}`}>{fmtFocus(k, s.focus[k])}</div>
          <div className="mt-0.5 text-[9px] leading-tight text-slate-400">{SHORT[k]}</div>
          <TBar p={s.targets?.[k]} k={k} testid={`ksup-tbar-${s.name}-${k}`} />
        </div>
      ))}
    </div>
  </button>
);

export const KeetaSupervisors = ({ platform, canEdit }) => {
  const [month, setMonth] = useState("");
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [drillK, setDrillK] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tOpen, setTOpen] = useState(false);
  const fileRef = useRef(null);
  const load = useCallback((m) => api.keetaSupList(platform, m).then((d) => { setData(d); setMonth(d.month); }).catch(() => toast.error("تعذّر التحميل")), [platform]);
  useEffect(() => { setOpen(null); load(); }, [load]);

  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const r = await api.keetaSupUpload(platform, f);
      toast.success(`تم رفع ${r.rows} صف · ${r.supervisors} مشرف${r.new_supervisors ? ` (${r.new_supervisors} جديد)` : ""}${r.new_columns ? ` · ${r.new_columns} مؤشر جديد` : ""}`);
      load(r.to.slice(0, 7));
    } catch (e2) { toast.error(e2?.response?.data?.detail || "تعذّر الرفع"); } finally { setBusy(false); }
  };
  const delMonth = async () => {
    if (!window.confirm(`حذف تقرير المشرفين لشهر ${month}؟`)) return;
    await api.keetaSupDeleteMonth(platform, month); toast.success("تم الحذف"); load();
  };

  if (open) return <KeetaSupervisorPage platform={platform} name={open} month={month} canEdit={canEdit} onBack={() => { setOpen(null); load(month); }} />;
  return (
    <div className="space-y-4" data-testid="ksup-panel">
      {month && <UploadTracker params={{ section: "keeta_sup", platform, month }} rev={data} title="رفع تقرير المشرفين" testid="ut-ksup" canEdit={canEdit} onDeleted={() => load(month)} />}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2"><div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-400"><Users className="h-5 w-5 text-slate-900" /></div>
          <div><p className="font-heading font-bold text-slate-800">تقرير المشرفين</p><p className="text-xs text-slate-400">اضغط على المشرف لفتح صفحته</p></div></div>
        <div className="flex flex-wrap items-center gap-2">
          {data && <MonthSel months={data.months} value={month} onChange={load} />}
          {canEdit && data?.supervisors?.length > 0 && <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setTOpen(true)} data-testid="ksup-targets-btn"><Target className="h-4 w-4" /> أهداف المشرفين</Button>}
          {canEdit && data?.months?.length > 0 && <Button size="sm" variant="outline" className="gap-1 text-rose-600" onClick={delMonth} data-testid="ksup-delete-month"><Trash2 className="h-4 w-4" /></Button>}
          {canEdit && <Button size="sm" className="gap-1.5" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="ksup-upload">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} رفع تقرير المشرفين</Button>}
          <input ref={fileRef} type="file" accept=".xlsx,.xls" hidden onChange={upload} data-testid="ksup-file" />
        </div>
      </div>
      {!data ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-300" /> : !data.supervisors.length ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-400" data-testid="ksup-empty">ارفع تقرير المشرفين من كيتا، وكل مشرف في الملف تنشأ له صفحة تلقائياً.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6" data-testid="ksup-totals">
            {FOCUS.map((k) => (
              <button type="button" key={k} onClick={() => setDrillK(k)} data-testid={`ksup-total-${k}`} className="rounded-2xl border border-slate-200 bg-white p-3 text-start transition hover:border-amber-300 hover:shadow-md">
                <div className="text-[11px] text-slate-400">{SHORT[k]} {k === "on_time" || k === "avg_duration" ? "(متوسط)" : "(إجمالي)"}</div>
                <div className={`mt-1 text-xl font-bold tabular-nums ${TONE[k]}`}>{fmtFocus(k, data.totals[k])}</div>
              </button>
            ))}
            {data.supervisors.some((s) => s.team_income != null) && (
              <button type="button" onClick={() => setDrillK("team_income")} data-testid="ksup-total-team_income" className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-3 text-start transition hover:border-emerald-400 hover:shadow-md">
                <div className="text-[11px] text-emerald-700">دخل الفرق من الفاتورة (إجمالي)</div>
                <div className="mt-1 text-xl font-bold tabular-nums text-emerald-700">{data.supervisors.some((s) => s.income_hidden) ? <Blur id="ksup-total-income-hidden" /> : n(data.supervisors.reduce((a, s) => a + (s.team_income || 0), 0))} <span className="text-xs">ر.س</span></div>
              </button>
            )}
          </div>
          <TotalsDrill k={drillK} list={data.supervisors} onClose={() => setDrillK(null)} onPick={(nm) => { setDrillK(null); setOpen(nm); }} />
          <Podium list={data.supervisors} onOpen={setOpen} />
          <div className="grid gap-3 md:grid-cols-2">{data.supervisors.map((s) => <SupCard key={s.name} s={s} onOpen={() => setOpen(s.name)} />)}</div>
          <TargetsDialog open={tOpen} onClose={() => setTOpen(false)} platform={platform} month={month} names={data.supervisors.filter((s) => !s.tag).map((s) => s.name)} onSaved={() => load(month)} />
        </>
      )}
    </div>
  );
};

const LinkEmp = ({ platform, name, value, onDone }) => {
  const [emps, setEmps] = useState(null);
  useEffect(() => { api.listEmployees().then((l) => setEmps(Array.isArray(l) ? l : l.items || [])).catch(() => setEmps([])); }, []);
  const save = async (id) => { await api.keetaSupLink(platform, name, id || null); toast.success(id ? "تم ربط المشرف بالموظف" : "تم إلغاء الربط"); onDone(); };
  return (
    <div className="flex items-center gap-2"><UserCheck className="h-4 w-4 text-slate-400" />
      <select value={value || ""} onChange={(e) => save(e.target.value)} disabled={!emps} className="h-9 max-w-[220px] rounded-lg border border-slate-200 bg-white px-2 text-sm" data-testid="ksup-link-select">
        <option value="">بدون ربط بموظف</option>
        {(emps || []).filter(isActiveEmp).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
      </select>
    </div>
  );
};

const esc = (x) => String(x ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const printSupervisor = (d) => {
  const s = d.supervisor, fk = d.focus_keys;
  const medal = d.rank ? (MEDALS[d.rank] ? `${MEDALS[d.rank][0]} · المركز ${d.rank} من ${d.ranked}` : `المركز ${d.rank} من ${d.ranked}`) : "";
  const kpis = FOCUS.map((k) => `<div class="k"><div class="l">${esc(d.focus_labels[k])}</div><div class="v">${esc(fmtFocus(k, d.focus[k]))}</div></div>`).join("");
  const groups = d.groups.filter((g) => g.metrics.length).map((g) => `<h3>${esc(g.group)}</h3><div class="g">${g.metrics.map((m) => `<div><span>${esc(m.label)}</span><b>${esc(fmtMetric(m.value, m.rate, m.label))}</b></div>`).join("")}</div>`).join("");
  const cols = FOCUS.map((k) => fk[k]).filter(Boolean);
  const colDef = Object.fromEntries(d.columns.map((c) => [c.key, c]));
  const rows = d.daily.map((r) => `<tr><td>${r.date.slice(5)}</td>${cols.map((c) => `<td>${esc(fmtMetric(r.metrics[c], colDef[c]?.rate, colDef[c]?.label))}</td>`).join("")}</tr>`).join("");
  const html = `<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"><title>${esc(s.name)} - ${d.month}</title>
<style>@page{size:A4;margin:10mm}*{box-sizing:border-box}body{font-family:Tahoma,Arial,sans-serif;color:#0f172a;font-size:10px;margin:0}
.hd{display:flex;justify-content:space-between;align-items:center;border-bottom:3px solid #F5C400;padding-bottom:6px;margin-bottom:8px}
h1{font-size:18px;margin:0}h3{font-size:11px;margin:8px 0 4px;color:#475569}.m{background:#FEF3C7;border-radius:20px;padding:3px 10px;font-weight:bold}
.ks{display:grid;grid-template-columns:repeat(5,1fr);gap:5px}.k{border:1.5px solid #FCD34D;border-radius:8px;padding:6px;text-align:center}.k .l{font-size:8.5px;color:#64748b;min-height:22px}.k .v{font-size:15px;font-weight:bold}
.g{display:grid;grid-template-columns:repeat(4,1fr);gap:3px}.g div{display:flex;justify-content:space-between;gap:4px;border-bottom:1px solid #f1f5f9;padding:2px 3px;font-size:8.5px}.g span{color:#64748b}
table{width:100%;border-collapse:collapse;font-size:8.5px}th,td{border:1px solid #e2e8f0;padding:2px 4px;text-align:center}th{background:#f8fafc}
.ft{margin-top:6px;color:#94a3b8;font-size:8px;text-align:center}</style></head><body>
<div class="hd"><div><h1>${esc(s.name)}</h1><div>تقرير أداء المشرف · كيتا شرائح · شهر ${esc(d.month)} · ${d.daily.length} يوم</div></div><div style="text-align:left">${medal ? `<span class="m">${esc(medal)}</span>` : ""}<div style="margin-top:4px">${esc(d.company)}</div></div></div>
<div class="ks">${kpis}</div>${groups}
<h3>المؤشرات الرئيسية يومياً</h3><table><thead><tr><th>اليوم</th>${cols.map((c) => `<th>${esc(colDef[c]?.label)}</th>`).join("")}</tr></thead><tbody>${rows}</tbody></table>
<div class="ft">صادر من WorkZone · ${new Date().toLocaleDateString("en-GB")}</div><script>onload=()=>setTimeout(()=>print(),300)</script></body></html>`;
  const w = window.open("", "_blank");
  if (!w) return toast.error("اسمح بالنوافذ المنبثقة للطباعة");
  w.document.write(html); w.document.close();
};

const DailyChart = ({ daily, fk }) => {
  const rows = daily.map((d) => ({ date: d.date.slice(8), delivered: d.metrics[fk.delivered] ?? null, on_time: d.metrics[fk.on_time] != null ? +(d.metrics[fk.on_time] * 100).toFixed(2) : null, late: d.metrics[fk.late] ?? null }));
  return (
    <div className="h-64 rounded-2xl border border-slate-200 bg-white p-3" data-testid="ksup-chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={rows} margin={{ left: 0, right: 8, top: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="date" tick={{ fontSize: 10 }} reversed />
          <YAxis yAxisId="a" tick={{ fontSize: 10 }} orientation="right" />
          <YAxis yAxisId="b" tick={{ fontSize: 10 }} orientation="left" domain={[90, 100]} />
          <Tooltip />
          <Line yAxisId="a" dataKey="delivered" name="المهام المسلّمة" stroke="#059669" strokeWidth={2} dot={false} />
          <Line yAxisId="a" dataKey="late" name="المتأخرة" stroke="#ea580c" strokeWidth={2} dot={false} />
          <Line yAxisId="b" dataKey="on_time" name="في الوقت %" stroke="#2563eb" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const BAD_RE = /رفض|مرفوض|متأخر|إلغاء|الغاء|انتظار|مدة|وقت وصول|وقت تسليم|أكثر من/;
const drillSpec = (d, k) => {
  if (!k) return null;
  if (k.startsWith("col:")) {
    const key = k.slice(4);
    const m = d.groups.flatMap((g) => g.metrics).find((x) => x.key === key);
    if (!m) return null;
    const lbl = m.label.trim();
    return { key, title: lbl, fmt: (v) => fmtMetric(v, m.rate, lbl), good: !BAD_RE.test(lbl), total: m.value, rv: (r) => r.kv?.[lbl] ?? r.kv?.[lbl.replace(/\s*\(D\)\s*/, " ").trim()] };
  }
  return { key: d.focus_keys[k], title: d.focus_labels[k], fmt: (v) => fmtFocus(k, v), good: k === "delivered" || k === "on_time", total: d.focus[k], rv: (r) => r.focus?.[k], tone: TONE[k] };
};

const FocusDrill = ({ d, k, onClose }) => {
  const sp = drillSpec(d, k);
  const key = sp?.key;
  const rows = sp ? d.daily.filter((r) => r.metrics[key] != null).map((r) => ({ date: r.date, v: Number(r.metrics[key]) })) : [];
  const max = Math.max(1, ...rows.map((r) => Math.abs(r.v)));
  const good = !!sp?.good;
  const sorted = [...rows].sort((a, b) => a.v - b.v);
  const best = good ? sorted[sorted.length - 1] : sorted[0];
  const worst = good ? sorted[0] : sorted[sorted.length - 1];
  const tgt = k && !k.startsWith("col:") && d.targets?.[k];
  const fmtV = (v) => (sp ? sp.fmt(v) : "");
  const [byRider, setByRider] = useState(false);
  useEffect(() => { setByRider(false); }, [k]);
  const riders = sp ? (d.riders || []).map((r) => ({ ...r, v: Number(sp.rv(r)) })).filter((r) => sp.rv(r) != null && r.v !== 0 && !Number.isNaN(r.v))
    .sort((a, b) => b.v - a.v) : [];
  const rmax = Math.max(1, ...riders.map((r) => Math.abs(r.v)));
  return (
    <Dialog open={!!k} onOpenChange={(v) => !v && onClose()}>
      <DialogContent dir="rtl" className="max-h-[88vh] max-w-lg overflow-y-auto" data-testid="ksup-drill">
        <DialogHeader className="text-start"><DialogTitle>{sp?.title} — {d.supervisor.name}</DialogTitle>
          <DialogDescription className="text-start">الإجمالي للشهر {d.month}: <b className={sp?.tone || "text-slate-800"}>{sp && fmtV(sp.total)}</b>{tgt?.target != null ? <> · الهدف: <b>{fmtV(tgt.target)}</b></> : null}</DialogDescription></DialogHeader>
        {rows.length > 0 && (
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="rounded-lg bg-emerald-50 p-2" data-testid="ksup-drill-best">أفضل يوم: <b>{best.date}</b> ({fmtV(best.v)})</div>
            <div className="rounded-lg bg-rose-50 p-2" data-testid="ksup-drill-worst">أضعف يوم: <b>{worst.date}</b> ({fmtV(worst.v)})</div>
          </div>
        )}
        <button type="button" onClick={() => setByRider((x) => !x)} data-testid="ksup-drill-by-rider"
          className={`flex w-full items-center justify-center gap-1.5 rounded-lg border py-2 text-xs font-bold ${byRider ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-600"}`}>
          <Users className="h-4 w-4" /> {byRider ? "عرض حسب الأيام" : `حسب المندوب (${riders.length})`}
        </button>
        {byRider ? (
          <div className="space-y-1" data-testid="ksup-drill-riders">
            {!riders.length ? <p className="py-4 text-center text-xs text-slate-400">لا يوجد مناديب لديهم قيمة في هذا المؤشر</p> : riders.map((r) => (
              <div key={r.rider_id} className="flex items-center gap-2 text-xs" data-testid={`ksup-drill-rider-${r.rider_id}`}>
                <span className="w-28 shrink-0 truncate font-semibold text-slate-700" title={r.name}>{r.name || r.rider_id}</span>
                <div className="h-4 flex-1 overflow-hidden rounded bg-slate-100"><div className={`h-full rounded ${good ? "bg-emerald-400" : "bg-orange-400"}`} style={{ width: `${(Math.abs(r.v) / rmax) * 100}%` }} /></div>
                <span className="w-16 text-left font-semibold tabular-nums">{fmtV(r.v)}</span>
              </div>
            ))}
          </div>
        ) : (
        <div className="space-y-1" data-testid="ksup-drill-rows">
          {!rows.length ? <p className="py-4 text-center text-xs text-slate-400">لا توجد بيانات يومية لهذا المؤشر</p> : rows.map((r) => (
            <div key={r.date} className="flex items-center gap-2 text-xs">
              <span className="w-20 shrink-0 tabular-nums text-slate-500">{r.date.slice(5)}</span>
              <div className="h-4 flex-1 overflow-hidden rounded bg-slate-100"><div className={`h-full rounded ${good ? "bg-emerald-400" : "bg-orange-400"}`} style={{ width: `${(Math.abs(r.v) / max) * 100}%` }} /></div>
              <span className="w-16 text-left font-semibold tabular-nums">{fmtV(r.v)}</span>
            </div>
          ))}
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const RiderAssign = ({ platform, r, d, isPool, onDone }) => {
  const assign = async (sup) => {
    if (!sup) return;
    try { await api.keetaRiderAssign(platform, { rider_id: String(r.rider_id), supervisor: sup, month: d.month }); toast.success(`تم تعيين ${r.name || r.rider_id} للمشرف ${sup}`); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التعيين"); }
  };
  const unassign = async () => {
    try { await api.keetaRiderUnassign(platform, String(r.rider_id)); toast.success("تم إلغاء التعيين اليدوي"); onDone(); }
    catch { toast.error("تعذّر الإلغاء"); }
  };
  if (isPool) return (
    <select defaultValue="" onChange={(e) => assign(e.target.value)} className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs" data-testid={`ksup-assign-${r.rider_id}`}>
      <option value="" disabled>تعيين لمشرف…</option>
      {(d.assignable || []).map((x) => <option key={x} value={x}>{x}</option>)}
    </select>
  );
  if (!r.manual) return null;
  return (
    <span className="flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] text-blue-700" data-testid={`ksup-manual-${r.rider_id}`}>معيّن يدوياً
      <button onClick={unassign} className="font-bold text-rose-600" data-testid={`ksup-unassign-${r.rider_id}`}>إلغاء</button></span>
  );
};

const KeetaSupervisorPage = ({ platform, name, month: m0, canEdit, onBack }) => {
  const [d, setD] = useState(null);
  const [drill, setDrill] = useState(null);
  const load = useCallback((m) => api.keetaSupDetail(platform, name, m).then(setD).catch(() => toast.error("تعذّر التحميل")), [platform, name]);
  useEffect(() => { load(m0); }, [load, m0]);
  if (!d) return <Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-300" />;
  const s = d.supervisor;
  const focusKeys = new Set(Object.values(d.focus_keys));
  return (
    <div className="space-y-4" data-testid="ksup-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" className="h-9 w-9" onClick={onBack} data-testid="ksup-back"><ArrowRight className="h-4 w-4" /></Button>
          <div><p className="flex items-center gap-2 font-heading text-lg font-bold text-slate-800" data-testid="ksup-page-name">{s.name}<RankBadge rank={d.rank} name={`page-${s.name}`} />{s.tag && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500">{TAGS[s.tag]}</span>}</p>
            <p className="text-xs text-slate-400">{s.tag ? `يجمع: ${(s.members || []).join("، ")}` : "صفحة المشرف"} · {d.daily.length} يوم في {d.month}</p></div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canEdit && !s.tag && <LinkEmp platform={platform} name={s.name} value={s.employee_id} onDone={() => load(d.month)} />}
          <Button size="sm" variant="outline" className="h-9 gap-1.5" onClick={() => printSupervisor(d)} data-testid="ksup-print"><Printer className="h-4 w-4" /> طباعة PDF</Button>
          <MonthSel months={d.months} value={d.month} onChange={load} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5" data-testid="ksup-focus">
        {FOCUS.map((k) => (
          <button type="button" key={k} onClick={() => setDrill(k)} className="rounded-2xl border-2 border-amber-200 bg-amber-50/40 p-3 text-start transition-transform hover:-translate-y-0.5 hover:border-amber-300 hover:shadow-sm" data-testid={`ksup-focus-${k}`}>
            <div className="text-[11px] text-slate-500">{d.focus_labels[k]}</div>
            <div className={`mt-1 text-2xl font-bold tabular-nums ${TONE[k]}`}>{fmtFocus(k, d.focus[k])}</div>
            <TBar p={d.targets?.[k]} k={k} testid={`ksup-page-tbar-${k}`} />
          </button>
        ))}
      </div>
      <FocusDrill d={d} k={drill} onClose={() => setDrill(null)} />
      {d.daily.length > 0 && <DailyChart daily={d.daily} fk={d.focus_keys} />}
      {d.groups.filter((g) => g.metrics.length).map((g) => (
        <div key={g.group} className="rounded-2xl border border-slate-200 bg-white" data-testid={`ksup-group-${g.group}`}>
          <p className="border-b border-slate-100 px-4 py-2.5 font-heading text-sm font-bold text-slate-700">{g.group}</p>
          <div className="grid grid-cols-2 gap-px bg-slate-100 sm:grid-cols-3 lg:grid-cols-4">
            {g.metrics.map((m) => (
              <button type="button" key={m.key} onClick={() => setDrill(`col:${m.key}`)} data-testid={`ksup-metric-${m.key}`}
                className={`bg-white px-3 py-2.5 text-start transition-colors hover:bg-blue-50/50 ${focusKeys.has(m.key) ? "ring-1 ring-inset ring-amber-300" : ""}`}>
                <div className="text-[11px] leading-snug text-slate-400">{m.label}{m.kind === "avg" ? " (متوسط)" : ""}</div>
                <div className="mt-0.5 font-bold tabular-nums text-slate-800">{fmtMetric(m.value, m.rate, m.label)}</div>
              </button>
            ))}
          </div>
        </div>
      ))}
      <div className="rounded-2xl border border-slate-200 bg-white" data-testid="ksup-daily">
        <p className="border-b border-slate-100 px-4 py-2.5 font-heading text-sm font-bold text-slate-700">الجدول اليومي</p>
        <div className="overflow-x-auto">
          <table className="min-w-max text-xs">
            <thead className="bg-slate-50 text-slate-500"><tr><th className="sticky right-0 bg-slate-50 px-3 py-2 text-right">التاريخ</th>
              {d.columns.map((c) => <th key={c.key} className={`max-w-[120px] px-3 py-2 text-right font-medium ${focusKeys.has(c.key) ? "bg-amber-50 text-amber-800" : ""}`}>{c.label}</th>)}</tr></thead>
            <tbody>{d.daily.map((r) => (
              <tr key={r.date} className={`border-t border-slate-100 ${r.active ? "" : "text-slate-300"}`}>
                <td className="sticky right-0 bg-white px-3 py-1.5 font-semibold">{r.date}</td>
                {d.columns.map((c) => <td key={c.key} className={`px-3 py-1.5 tabular-nums ${focusKeys.has(c.key) ? "bg-amber-50/50 font-semibold" : ""}`}>{fmtMetric(r.metrics[c.key], c.rate, c.label)}</td>)}
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white" data-testid="ksup-riders">
        <p className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-2.5 font-heading text-sm font-bold text-slate-700">مناديب المشرف ({d.riders.length})
          {d.team?.with_targets > 0 && <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs text-emerald-700" data-testid="ksup-team-met">حقق كل الأهداف: {d.team.all_met} من {d.team.with_targets}</span>}</p>
        {!d.riders.length ? <p className="px-4 py-4 text-center text-xs text-slate-400">بيظهرون هنا أول ما ترفع تقرير المناديب وفيه اسم المشرف.</p> : (
          <div className="divide-y divide-slate-100">{d.riders.map((r) => (
            <div key={r.rider_id} className="flex items-center justify-between gap-2 px-4 py-2 text-sm"><span className="min-w-0 flex-1 truncate">{r.name || r.rider_id}</span>
              {r.total > 0 && <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${r.met === r.total ? "bg-emerald-100 text-emerald-700" : "bg-rose-50 text-rose-600"}`} data-testid={`ksup-rider-met-${r.rider_id}`}>{r.met}/{r.total}</span>}
              {canEdit && <RiderAssign platform={platform} r={r} d={d} isPool={Boolean(s.tag)} onDone={() => load(d.month)} />}
              <span className="w-14 text-left font-bold tabular-nums">{n(r.completed)}</span></div>
          ))}</div>
        )}
      </div>
    </div>
  );
};
